import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, User, ImageOff, ExternalLink, ShoppingCart } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { pageTitle, truncate, absoluteUrl, DEFAULT_OG_IMAGE } from "@/lib/seo";
import { productImage, productTitle, formatPrice } from "@/lib/helpers";
import { blogImage, blogLang, formatBlogDate } from "@/lib/blog";

const ArticleCta = ({ post, lang }) => {
  const type = post.cta_type;
  if (!type || type === "none") return null;
  const disclosure = blogLang(post, "cta_disclosure", lang);
  const Disc = () => disclosure ? <p className="mt-3 text-xs text-brand-navy/45" data-testid="blog-cta-disclosure">{disclosure}</p> : null;

  if ((type === "product" || type === "amazon") && post.cta_product) {
    const p = post.cta_product;
    const img = productImage(p, 400);
    const isAmazon = type === "amazon";
    return (
      <div data-testid="blog-cta" className="mt-10 rounded-2xl border border-brand-navy/10 bg-brand-light p-6 flex flex-col sm:flex-row items-center gap-5">
        <div className="h-24 w-24 rounded-xl bg-white border overflow-hidden flex items-center justify-center shrink-0">
          {img ? <img src={img} alt={productTitle(p, lang)} className="w-full h-full object-contain p-1" /> : <ImageOff className="h-8 w-8 text-brand-navy/30" />}
        </div>
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <h4 className="font-heading font-semibold text-brand-navy">{productTitle(p, lang)}</h4>
          {p.price > 0 && <p className="text-brand-navy/60 text-sm mt-1">{formatPrice(p.price)}{isAmazon ? (lang === "en" ? " (indicative)" : " (indicativo)") : ""}</p>}
          <Disc />
        </div>
        {isAmazon ? (
          <a data-testid="blog-cta-amazon" href={p.amazon_url} target="_blank" rel="noopener noreferrer"
            className="shrink-0 rounded-full h-11 px-6 inline-flex items-center gap-2 bg-[#FF9900] hover:bg-[#e88b00] text-brand-navy font-bold">
            <ExternalLink className="h-4 w-4" /> {lang === "en" ? "View on Amazon" : "Vedi su Amazon"}
          </a>
        ) : (
          <Link data-testid="blog-cta-product" to={`/prodotti/${p.id}`}
            className="shrink-0 rounded-full h-11 px-6 inline-flex items-center gap-2 bg-brand-electric hover:bg-brand-electric/90 text-white font-semibold">
            <ShoppingCart className="h-4 w-4" /> {lang === "en" ? "View product" : "Vedi prodotto"}
          </Link>
        )}
      </div>
    );
  }

  if (type === "external" && post.cta_url) {
    const title = blogLang(post, "cta_title", lang);
    const text = blogLang(post, "cta_text", lang);
    const label = blogLang(post, "cta_button", lang) || (lang === "en" ? "Discover more" : "Scopri di più");
    return (
      <div data-testid="blog-cta" className="mt-10 rounded-2xl bg-brand-navy text-white p-8 flex flex-col md:flex-row items-center justify-between gap-5">
        <div className="max-w-xl text-center md:text-left">
          {title && <h4 className="text-xl font-bold font-heading">{title}</h4>}
          {text && <p className="mt-2 text-white/70">{text}</p>}
          {disclosure && <p className="mt-3 text-xs text-white/45" data-testid="blog-cta-disclosure">{disclosure}</p>}
        </div>
        <a data-testid="blog-cta-external" href={post.cta_url} target={post.cta_new_tab ? "_blank" : undefined} rel="noopener noreferrer"
          className="shrink-0 rounded-full h-12 px-7 inline-flex items-center gap-2 bg-brand-electric hover:bg-brand-electric/90 text-white font-semibold">
          {label} <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    );
  }
  return null;
};

const BlogPost = () => {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    setPost(undefined);
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(null));
    api.get("/blog/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, [slug]);

  if (post === undefined) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-brand-navy/40">…</div>;
  if (post === null) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="blog-not-found">
      <h1 className="text-2xl font-heading font-bold text-brand-navy">{lang === "en" ? "Article not found" : "Articolo non trovato"}</h1>
      <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-brand-electric">{lang === "en" ? "Back to blog" : "Torna al blog"}</Link>
    </div>
  );

  const catName = (s) => { const c = cats.find((x) => x.slug === s); return c ? (lang === "en" ? c.name_en : c.name_it) : ""; };
  const title = blogLang(post, "title", lang);
  const excerpt = blogLang(post, "excerpt", lang);
  const content = lang === "en" ? (post.content_en || post.content_it) : (post.content_it || post.content_en);
  const img = blogImage(post.image_url);
  const seoTitle = blogLang(post, "seo_title", lang) || title;
  const seoDesc = blogLang(post, "seo_desc", lang) || excerpt;
  const catLabel = catName(post.category_slug);

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: title, description: seoDesc,
      image: img || DEFAULT_OG_IMAGE,
      author: { "@type": "Person", name: post.author || "LearnSphereHub" },
      datePublished: post.published_at, dateModified: post.updated_at || post.published_at,
      mainEntityOfPage: { "@type": "WebPage", "@id": absoluteUrl(`/blog/${post.slug}`) },
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
        { "@type": "ListItem", position: 2, name: "Blog", item: absoluteUrl("/blog") },
        { "@type": "ListItem", position: 3, name: title, item: absoluteUrl(`/blog/${post.slug}`) },
      ],
    },
  ];

  return (
    <div data-testid="blog-post-page">
      <Seo title={pageTitle(seoTitle)} description={truncate(seoDesc)} path={`/blog/${post.slug}`} image={img || undefined} type="article" jsonLd={jsonLd} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        <nav className="text-sm text-brand-navy/50 flex items-center gap-1.5 flex-wrap" data-testid="blog-breadcrumb">
          <Link to="/" className="hover:text-brand-electric">Home</Link><span>/</span>
          <Link to="/blog" className="hover:text-brand-electric">Blog</Link><span>/</span>
          <span className="text-brand-navy/70 truncate">{title}</span>
        </nav>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {catLabel && <span className="text-xs font-semibold uppercase tracking-wider text-brand-electric">{catLabel}</span>}
        <h1 className="mt-3 text-3xl md:text-4xl font-extrabold font-heading text-brand-navy tracking-tight leading-tight">{title}</h1>
        {excerpt && <p className="mt-4 text-lg text-brand-navy/60">{excerpt}</p>}
        <div className="mt-4 flex items-center gap-4 text-sm text-brand-navy/45">
          {post.author && <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author}</span>}
          {post.published_at && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {formatBlogDate(post.published_at, lang)}</span>}
          {post.updated_at && post.updated_at !== post.published_at && <span className="text-xs">({lang === "en" ? "updated" : "aggiornato"} {formatBlogDate(post.updated_at, lang)})</span>}
        </div>

        {img && (
          <div className="mt-8 rounded-2xl overflow-hidden border border-brand-navy/10">
            <img src={img} alt={title} className="w-full object-cover" />
          </div>
        )}

        <div className="blog-content mt-8 text-brand-navy/80 leading-relaxed" data-testid="blog-content"
          dangerouslySetInnerHTML={{ __html: content || "" }} />

        <ArticleCta post={post} lang={lang} />

        <div className="mt-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-brand-navy/60 hover:text-brand-electric">
            <ArrowLeft className="h-4 w-4" /> {lang === "en" ? "Back to blog" : "Torna al blog"}
          </Link>
        </div>
      </article>

      {post.related && post.related.length > 0 && (
        <section className="bg-brand-light border-t border-brand-navy/10" data-testid="blog-related">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
            <h2 className="text-2xl font-bold font-heading text-brand-navy">{lang === "en" ? "Related articles" : "Articoli correlati"}</h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-6">
              {post.related.map((r) => {
                const rimg = blogImage(r.image_url);
                return (
                  <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`}
                    className="group bg-white rounded-2xl border border-brand-navy/10 overflow-hidden lift flex flex-col">
                    <div className="aspect-[16/9] bg-brand-light overflow-hidden">
                      {rimg ? <img src={rimg} alt={blogLang(r, "title", lang)} loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-brand-navy/25"><ImageOff className="h-8 w-8" /></div>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-brand-navy leading-snug line-clamp-2">{blogLang(r, "title", lang)}</h3>
                      <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-electric group-hover:text-brand-orange">{lang === "en" ? "Read" : "Leggi"} <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
